import React, { useState, useRef, useEffect } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link,
  Collapse,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
  Sms as SmsIcon,
  Email as EmailIcon,
  PhoneInTalk as PhoneInTalkIcon,
  LocationOn as LocationOnIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useWhitelabel } from '../context/WhitelabelContext';
import { useNavigate } from 'react-router-dom';
import api, { handleCatch } from '../api';

const decodeJwtPayload = (token) => {
  const tokenParts = token.split('.');
  if (tokenParts.length !== 3) {
    throw new Error('Invalid token format');
  }

  const base64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
  const paddedBase64 = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  return JSON.parse(atob(paddedBase64));
};

const clearStoredAuthSession = () => {
  localStorage.removeItem('userToken');
  localStorage.removeItem('userId');
  localStorage.removeItem('userData');
  localStorage.removeItem('businessTimezone');
  sessionStorage.clear();
  window.NativeBridge?.clearSession?.();
};

const LoginScreen = () => {
  const [data, setData] = useState({
    username: '',
    password: '',
    check_textInputChange: false,
    secureTextEntry: true,
    isValidUser: true,
    isValidPassword: true,
  });
  const [error, setError] = useState('');
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);

  // Check for admin token in URL parameters for auto-login
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const adminToken = urlParams.get('adminToken');
    const forceLogin = urlParams.get('forceLogin') === '1';
    
    console.log('LoginScreen mounted, checking for adminToken...', { 
      hasToken: !!adminToken, 
      forceLogin,
      urlSearch: window.location.search 
    });
    
    if (adminToken) {
      console.log('Admin token found, initiating auto-login...');
      handleAdminTokenLogin(adminToken);
    }
  }, []);

  useEffect(() => {
    if (window.NativeBridge?.checkBiometric) {
      window.NativeBridge.checkBiometric().then(result => {
        setBiometricAvailable(!!result?.available);
      }).catch(() => {});
    }
  }, []);
  const [supportExpanded, setSupportExpanded] = useState(false);
  const { signIn } = useAuth();
  const { config: whitelabelConfig } = useWhitelabel();
  const navigate = useNavigate();

  // Extract whitelabel configuration
  const organisationName = whitelabelConfig?.organisationName || '';
  const logoUrl = whitelabelConfig?.logoUrl || '';
  const primaryColor = whitelabelConfig?.primaryColor || '#0066CC';
  const secondaryColor = whitelabelConfig?.secondaryColor || '#004C99';
  const supportEmail = whitelabelConfig?.supportEmail || '';
  const supportPhone = whitelabelConfig?.supportPhone || '';
  const supportAddress = whitelabelConfig?.supportAddress || '';

  const handleUserNameChange = (val) => {
    if (val.trim().length >= 10) {
      setData({
        ...data,
        username: val,
        check_textInputChange: true,
        isValidUser: true,
      });
    } else {
      setData({
        ...data,
        username: val,
        check_textInputChange: false,
        isValidUser: false,
      });
    }
    setError('');
  };

  const handlePasswordChange = (val) => {
    if (val.trim().length >= 1) {
      setData({
        ...data,
        password: val,
        isValidPassword: true,
      });
    } else {
      setData({
        ...data,
        password: val,
        isValidPassword: false,
      });
    }
    setError('');
  };

  const updateSecureTextEntry = () => {
    setData({
      ...data,
      secureTextEntry: !data.secureTextEntry,
    });
  };

  const loginHandle = async (e) => {
    e.preventDefault();
    setError('');

    if (data.username.length === 0) {
      setError('Mobile / Password field cannot be empty.');
      usernameRef.current?.focus();
      return;
    }
    if (data.password.length === 0) {
      setError('Mobile / Password field cannot be empty.');
      passwordRef.current?.focus();
      return;
    }

    if (!data.isValidUser) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }

    const header = {
      userId: data.username,
      userPassword: data.password,
    };

    setLoading(true);
    try {
      const response = await api.post('api/TOKEN/get-client-token', header);
      const token = response.data.token;
      const businessTimezone = response.data.businessTimezone || null;
      await signIn(data.username, token, businessTimezone);

      // Fetch student name for biometric session display
      let studentName = null;
      try {
        const profileRes = await api.get('api/master/student/get-student-profile', {
          headers: { Authorization: `Bearer ${token}` },
        });
        studentName = profileRes.data?.studentName || null;
      } catch (_) {}

      // Notify the native shell so it can persist the session
      window.NativeBridge?.saveSession({ token, name: studentName });
      navigate('/home', { replace: true });
    } catch (error) {
      const errorMsg = handleCatch(error);
      setError(errorMsg || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminTokenLogin = async (token) => {
    setLoading(true);
    setError('');
    
    console.log('Starting admin token login...', { tokenLength: token?.length });

    try {
      clearStoredAuthSession();

      // Decode the JWT token to extract username
      const payload = decodeJwtPayload(token);
      console.log('Token payload decoded:', payload);
      
      const username = payload.UserName || payload.sub || '';

      if (!username) {
        throw new Error('Unable to extract username from token');
      }

      console.log('Extracted username:', username);
      console.log('Calling signIn...');

      // Sign in with the admin-generated token (this stores it in localStorage)
      // Note: Admin token login doesn't have businessTimezone in response, will use null
      await signIn(username, token, null);
      
      console.log('signIn completed, token stored in localStorage');
      console.log('localStorage after signIn:', {
        userToken: localStorage.getItem('userToken')?.substring(0, 50) + '...',
        userId: localStorage.getItem('userId')
      });
      
      // Notify native bridge
      window.NativeBridge?.saveSession({ token, name: username });
      
      console.log('Navigating to /home...');
      // Navigate to home
      navigate('/home', { replace: true });
      
      console.log('Navigation called');
    } catch (error) {
      console.error('Admin token login failed:', error);
      setError('Auto-login failed: ' + (error.message || 'Invalid token'));
      setLoading(false);
    }
  };

  const loginOTPHandle = async () => {
    setError('');

    if (data.username.length === 0) {
      setError('Mobile number is required.');
      return;
    }

    if (!data.isValidUser) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }

    const header = {
      mobile: data.username,
    };

    setLoading(true);
    try {
      const response = await api.post('api/TOKEN/generate-otp-client-token', header);
      navigate('/verify-otp', {
        state: {
          mobile: data.username,
          secretKey: response.data,
        },
      });
    } catch (error) {
      const errorMsg = handleCatch(error);
      setError(errorMsg || 'Failed to generate OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: primaryColor }}>

      {/* ── Top gradient banner ── */}
      <Box
        sx={{
          background: `linear-gradient(160deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
          px: 3,
          pt: 4,
          pb: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {logoUrl && (
          <img
            src={logoUrl}
            alt={organisationName}
            style={{ maxHeight: 80, maxWidth: '70%', objectFit: 'contain', marginBottom: 14 }}
          />
        )}
        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 22, textAlign: 'center' }}>
          Welcome Back!
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, mt: 0.5, textAlign: 'center' }}>
          Sign in to access your student portal
        </Typography>
        {organisationName && (
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, mt: 0.5 }}>
            {organisationName}
          </Typography>
        )}
      </Box>

      {/* ── White form card (scrollable) ── */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          backgroundColor: '#fff',
          borderRadius: '20px 20px 0 0',
          px: 2.5,
          pt: 3,
          pb: 4,
          mt: '-20px',
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={loginHandle} noValidate>
          {/* Mobile Number */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ mb: 0.75, fontWeight: 600, fontSize: 13, color: '#333' }}>
              Mobile Number
            </Typography>
            <TextField
              fullWidth
              inputRef={usernameRef}
              placeholder="Enter your mobile number"
              value={data.username}
              onChange={(e) => handleUserNameChange(e.target.value)}
              error={!data.isValidUser && data.username.length > 0}
              helperText={
                !data.isValidUser && data.username.length > 0
                  ? 'Mobile number must be 10 digits long.'
                  : ''
              }
              inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]*' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon sx={{ color: primaryColor }} />
                  </InputAdornment>
                ),
                endAdornment: data.check_textInputChange && (
                  <InputAdornment position="end">
                    <CheckCircleIcon sx={{ color: '#4CAF50' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#f8f9fa',
                  '& fieldset': { borderColor: '#e9ecef' },
                  '&:hover fieldset': { borderColor: primaryColor },
                  '&.Mui-focused fieldset': { borderColor: primaryColor },
                  '&.Mui-error': { backgroundColor: '#FFF5F5' },
                },
              }}
            />
          </Box>

          {/* Password */}
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ mb: 0.75, fontWeight: 600, fontSize: 13, color: '#333' }}>
              Password
            </Typography>
            <TextField
              fullWidth
              inputRef={passwordRef}
              type={data.secureTextEntry ? 'password' : 'text'}
              placeholder="Enter your password"
              value={data.password}
              onChange={(e) => handlePasswordChange(e.target.value)}
              error={!data.isValidPassword && data.password.length > 0}
              helperText={
                !data.isValidPassword && data.password.length > 0
                  ? 'Password is required.'
                  : ''
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: primaryColor }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={updateSecureTextEntry} edge="end" sx={{ color: '#999' }}>
                      {data.secureTextEntry ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#f8f9fa',
                  '& fieldset': { borderColor: '#e9ecef' },
                  '&:hover fieldset': { borderColor: primaryColor },
                  '&.Mui-focused fieldset': { borderColor: primaryColor },
                  '&.Mui-error': { backgroundColor: '#FFF5F5' },
                },
              }}
            />
          </Box>

          {/* Forgot Password */}
          <Box sx={{ textAlign: 'right', mb: 2.5 }}>
            <Link
              component="button"
              type="button"
              onClick={() => navigate('/forgot-password')}
              sx={{
                color: primaryColor,
                fontWeight: 600,
                fontSize: 13,
                textDecoration: 'none',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Forgot password?
            </Link>
          </Box>

          {/* Sign In button */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              height: 50,
              borderRadius: 25,
              fontSize: 16,
              fontWeight: 700,
              textTransform: 'none',
              backgroundColor: primaryColor,
              boxShadow: `0 4px 14px ${primaryColor}55`,
              '&:hover': { backgroundColor: secondaryColor },
              '&:disabled': { backgroundColor: '#ccc' },
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Sign In'}
          </Button>

          {/* Fingerprint / Biometric login */}
          {biometricAvailable && (
            <Button
              fullWidth
              variant="outlined"
              onClick={() => window.NativeBridge?.triggerBiometric()}
              disabled={loading}
              sx={{
                mt: 1.5,
                height: 50,
                borderRadius: 25,
                fontSize: 15,
                fontWeight: 600,
                textTransform: 'none',
                borderWidth: 2,
                borderColor: '#4CAF50',
                color: '#4CAF50',
                '&:hover': { borderWidth: 2, borderColor: '#388E3C', backgroundColor: '#4CAF5010' },
              }}
            >
              👆&nbsp;&nbsp;Login with Fingerprint
            </Button>
          )}

          {/* Sign In Using OTP */}
          <Button
            fullWidth
            variant="outlined"
            onClick={loginOTPHandle}
            disabled={loading}
            startIcon={<SmsIcon />}
            sx={{
              mt: 1.5,
              height: 50,
              borderRadius: 25,
              fontSize: 15,
              fontWeight: 600,
              textTransform: 'none',
              borderWidth: 2,
              borderColor: primaryColor,
              color: primaryColor,
              '&:hover': { borderWidth: 2, borderColor: secondaryColor, backgroundColor: `${primaryColor}0A` },
            }}
          >
            Sign In Using OTP
          </Button>

          {/* Version */}
          <Typography sx={{ mt: 2.5, textAlign: 'center', fontSize: 12, color: '#bbb' }}>
            Version 74.0 Last updated on 24th April 2026
          </Typography>

          {/* Support — collapsible */}
          {(supportEmail || supportPhone || supportAddress) && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
              <Box
                onClick={() => setSupportExpanded(!supportExpanded)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  gap: 0.75,
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#666' }}>Need Help?</Typography>
                <ExpandMoreIcon
                  sx={{
                    fontSize: 20,
                    color: '#666',
                    transform: supportExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.25s',
                  }}
                />
              </Box>
              <Collapse in={supportExpanded} timeout={250}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}>
                  {supportEmail && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <EmailIcon sx={{ fontSize: 17, color: primaryColor }} />
                      <Typography sx={{ fontSize: 13, color: '#555' }}>{supportEmail}</Typography>
                    </Box>
                  )}
                  {supportPhone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <PhoneInTalkIcon sx={{ fontSize: 17, color: primaryColor }} />
                      <Typography sx={{ fontSize: 13, color: '#555' }}>{supportPhone}</Typography>
                    </Box>
                  )}
                  {supportAddress && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <LocationOnIcon sx={{ fontSize: 17, color: primaryColor }} />
                      <Typography sx={{ fontSize: 13, color: '#555' }}>{supportAddress}</Typography>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default LoginScreen;

